import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Info as InfoIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';

const InterestConfiguration = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applyingInterest, setApplyingInterest] = useState(false);
  const [config, setConfig] = useState({
    overdueInterestRate: 5,
    interestCycleDays: 10,
    enabled: true,
    description: '',
  });

  useEffect(() => {
    fetchConfiguration();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig((prevConfig) => ({
      ...prevConfig,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value, 10) || 0 : value),
    }));
  };

  const handleRateChange = (e) => {
    const value = parseFloat(e.target.value);
    setConfig((prev) => ({
      ...prev,
      overdueInterestRate: Number.isNaN(value) ? 0 : value,
    }));
  };

  const fetchConfiguration = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/interest-configuration');
      const data = response.data;
      setConfig({
        overdueInterestRate: data.overdueInterestRate ?? 5,
        interestCycleDays: data.interestCycleDays ?? 10,
        enabled: data.enabled !== undefined ? data.enabled : true,
        description: data.description || '',
      });
    } catch (error) {
      console.error('Error fetching interest configuration:', error);
      showToast('Error fetching interest configuration: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (config.overdueInterestRate === undefined || config.overdueInterestRate === null || config.overdueInterestRate < 0) {
      showToast('Please enter a valid interest rate (0 or greater)', 'error');
      return;
    }

    if (!config.interestCycleDays || config.interestCycleDays < 1) {
      showToast('Interest cycle days must be at least 1', 'error');
      return;
    }

    if (!user || !user.userId) {
      showToast('Admin user not found. Cannot save configuration.', 'error');
      return;
    }

    try {
      setSaving(true);
      const params = new URLSearchParams();
      params.append('overdueInterestRate', String(parseFloat(config.overdueInterestRate) || 0));
      params.append('interestCycleDays', String(parseInt(config.interestCycleDays, 10) || 10));
      params.append('enabled', String(config.enabled !== undefined ? config.enabled : true));
      if (config.description) {
        params.append('description', config.description);
      }
      params.append('adminId', String(user.userId));

      await api.post(`/admin/interest-configuration?${params.toString()}`);
      showToast('Interest configuration updated successfully', 'success');
      await fetchConfiguration();
    } catch (error) {
      console.error('Error updating interest configuration:', error);
      showToast('Error: ' + (error.response?.data?.error || error.message || 'Failed to update interest configuration'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyInterest = async () => {
    if (!config.enabled) {
      showToast('Please enable post-maturity interest before applying', 'warning');
      return;
    }

    if (!config.overdueInterestRate || config.overdueInterestRate <= 0) {
      showToast('Please set a valid interest rate (greater than 0) before applying', 'warning');
      return;
    }

    try {
      setApplyingInterest(true);
      await api.post('/admin/interest/apply-now');
      showToast('Post-maturity interest calculation completed for all eligible loans', 'success');
    } catch (error) {
      console.error('Error applying interest:', error);
      showToast('Error: ' + (error.response?.data?.error || error.message || 'Failed to apply interest'), 'error');
    } finally {
      setApplyingInterest(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#0D47A1', mb: 3 }}>
        Interest Configuration
      </Typography>

      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <InfoIcon sx={{ color: '#0D47A1', mr: 1.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D47A1' }}>
              Post-Maturity Interest Rules
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Alert severity="info">
            <Typography variant="body2" component="div">
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>No late interest is charged before the loan&apos;s scheduled end date (final installment due date), even if installments are overdue.</li>
                <li>Starting the day after the loan end date, {config.overdueInterestRate || 5}% is applied once on the total outstanding principal balance.</li>
                <li>Every {config.interestCycleDays || 10} days after that, another {config.overdueInterestRate || 5}% is applied on the full outstanding balance (principal plus accumulated interest).</li>
                <li>Payments reduce principal and post-maturity interest; future cycles use the remaining balance.</li>
              </ul>
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.enabled}
                  onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Enable Post-Maturity Interest
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Applies automatically after the loan end date via the daily scheduled job
                  </Typography>
                </Box>
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              name="overdueInterestRate"
              label="Interest Rate (%) per cycle"
              type="number"
              value={config.overdueInterestRate}
              onChange={handleRateChange}
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Default 5% — applied on outstanding balance each cycle"
              disabled={!config.enabled}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              name="interestCycleDays"
              label="Days Between Interest Cycles"
              type="number"
              value={config.interestCycleDays}
              onChange={handleChange}
              inputProps={{ min: 1, step: 1 }}
              helperText="After the first charge (day after loan end), repeat every N days (default 10)"
              disabled={!config.enabled}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              name="description"
              label="Description"
              multiline
              rows={3}
              value={config.description}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="success"
                onClick={handleApplyInterest}
                disabled={saving || applyingInterest || !config.enabled}
                startIcon={applyingInterest ? <CircularProgress size={20} /> : <PlayArrowIcon />}
              >
                Run Interest Calculation Now
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={saving || applyingInterest}
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                Save Configuration
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default InterestConfiguration;
